import React, { forwardRef, useEffect, useRef, useState } from "react";
import Input from "./Input";
import Icon from "./Icon";
import "./SearchInput.scss";

// Fontaine M19 — SearchInput: the canonical search bar (V2 `search-default` / `search-filled`).
// Pill Input with a magnifier, clear ×, debounced onSearch, loading indicator, Esc clears,
// ⌘K / Ctrl+K opens the command palette. Full-width on phones, elastic on a filter row on laptop.

export const COMMAND_PALETTE_EVENT = "ft:command-palette";

/**
 * @param {string} value                              controlled text (shown immediately)
 * @param {(event) => void} [onChange]                raw change (every keystroke)
 * @param {(query: string) => void} [onSearch]        debounced — fire the request here
 * @param {number} [debounce=300]
 * @param {() => void} [onClear]                      × and Esc
 * @param {boolean} [loading]                         shows a spinner in place of the magnifier
 * @param {boolean} [auto]                            size to content instead of absorbing the row
 * @param {boolean} [commandPalette]                  ⌘K / Ctrl+K dispatches COMMAND_PALETTE_EVENT and shows the hint
 * @param {"md"|"lg"} [size="lg"]                     40 · 44 (legacy sm/xl/2xl mapped by Input)
 */
const SearchInput = forwardRef(function SearchInput(
  { value, onChange, onSearch, debounce = 300, onClear, loading = false, auto = false, commandPalette = false, size = "lg", placeholder = "Search", className = "", onKeyDown, onFocus, ...rest },
  ref
) {
  const [inner, setInner] = useState(value ?? "");
  const controlled = value !== undefined;
  const text = controlled ? value : inner;
  const first = useRef(true);

  useEffect(() => {
    if (!onSearch) return undefined;
    if (first.current) { first.current = false; return undefined; }
    const t = setTimeout(() => onSearch(text), debounce);
    return () => clearTimeout(t);
  }, [text, debounce, onSearch]);

  useEffect(() => {
    if (!commandPalette) return undefined;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent(COMMAND_PALETTE_EVENT));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [commandPalette]);

  const handleChange = (e) => {
    if (!controlled) setInner(e.target.value);
    onChange?.(e);
  };
  const clear = () => {
    if (!controlled) setInner("");
    if (onClear) onClear();
    else onChange?.({ target: { value: "" }, currentTarget: { value: "" } });
  };
  const handleKeyDown = (e) => {
    if (e.key === "Escape" && text) { e.preventDefault(); clear(); }
    onKeyDown?.(e);
  };

  // A `commandPalette` field is a TRIGGER, not a place to type: focusing it hands the query over to
  // the palette. It must therefore give the focus straight back, because the palette restores focus
  // to its opener on close — and a field that opens on focus would re-open it the moment Escape or
  // the scrim closed it. Blurring here breaks that loop at its source, so every topbar gets the
  // behaviour right without repeating the handler.
  const handleFocus = (e) => {
    if (commandPalette) {
      e.target.blur();
      window.dispatchEvent(new CustomEvent(COMMAND_PALETTE_EVENT));
    }
    onFocus?.(e);
  };

  return (
    <Input
      ref={ref}
      size={size}
      round
      clearable
      value={text}
      onChange={handleChange}
      onClear={clear}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      placeholder={placeholder}
      enterKeyHint="search"
      autoComplete="off"
      className={["ft-search", auto ? "ft-search--auto" : "", className].filter(Boolean).join(" ")}
      prefix={
        loading ? (
          <span className="ft-search__spin" aria-label="Searching" role="status"><Icon name="Loader2" size="sm" /></span>
        ) : (
          <Icon name="Search" size="sm" />
        )
      }
      suffix={commandPalette ? <kbd className="ft-search__kbd" aria-hidden="true">⌘K</kbd> : undefined}
      {...rest}
      // AFTER the spread on purpose. `DebounceInput` (react-debounce-input) carries a
      // `defaultProps.type = "text"`, so wrapping this component — as the Kanban board does —
      // silently downgraded the field to a plain textbox: no `searchbox` role, and the ×
      // labelled "Clear" instead of "Clear search". A SearchInput is a search field, always.
      type="search"
    />
  );
});

export default SearchInput;
